import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { cn } from "@veo/ui";

/**
 * Public form controls.
 *
 * Every field wires `aria-invalid` and `aria-describedby` to its error, and
 * errors carry `role="alert"`. On a form that is this business's only
 * conversion path, a validation message a screen-reader user never hears is a
 * lost enquiry, not a styling detail.
 */

const control =
  "w-full rounded-md border-0 bg-white px-4 py-3 text-base text-ink-900 ring-1 ring-inset ring-ink-300 " +
  "placeholder:text-ink-400 focus:ring-2 focus:ring-inset focus:ring-navy-700 " +
  "aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-danger-500";

export function FormField({
  label,
  htmlFor,
  error,
  hint,
  required,
  children,
  className,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("space-y-2", className)}>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-navy-900">
        {label}
        {required ? (
          <span className="ml-1 text-danger-500" aria-hidden>
            *
          </span>
        ) : (
          <span className="ml-1.5 text-xs font-normal text-ink-500">(optional)</span>
        )}
      </label>
      {children}
      {hint && !error && (
        <p id={`${htmlFor}-hint`} className="text-xs text-ink-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${htmlFor}-error`} role="alert" className="text-xs font-medium text-danger-500">
          {error}
        </p>
      )}
    </div>
  );
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function TextInput({ className, ...props }, ref) {
    return <input ref={ref} className={cn(control, className)} {...props} />;
  },
);

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function TextArea({ className, ...props }, ref) {
  return <textarea ref={ref} rows={5} className={cn(control, className)} {...props} />;
});

export const SelectInput = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function SelectInput({ className, children, ...props }, ref) {
    return (
      <select ref={ref} className={cn(control, "pr-10", className)} {...props}>
        {children}
      </select>
    );
  },
);

/**
 * A radio group rendered as selectable cards.
 *
 * Native radios underneath, so keyboard navigation and screen-reader semantics
 * are the browser's, not ours. The cards are a visual layer over correct HTML
 * rather than a replacement for it.
 */
export function RadioCards<T extends string>({
  name,
  legend,
  options,
  value,
  onChange,
  error,
  columns = 2,
}: {
  name: string;
  legend: string;
  options: { value: T; label: string; description?: string; icon?: ReactNode }[];
  value: T | undefined;
  onChange: (value: T) => void;
  error?: string;
  columns?: 2 | 4;
}) {
  return (
    <fieldset>
      <legend className="mb-2 text-sm font-medium text-navy-900">
        {legend}
        <span className="ml-1 text-danger-500" aria-hidden>
          *
        </span>
      </legend>

      <div
        className={cn(
          "grid gap-2",
          columns === 4 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-1 sm:grid-cols-2",
        )}
      >
        {options.map((option) => (
          <label
            key={option.value}
            className={cn(
              "flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors",
              "has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-gold-600",
              value === option.value
                ? "border-navy-700 bg-navy-50 ring-1 ring-navy-700"
                : "border-ink-300 bg-white hover:border-navy-400",
            )}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              className="sr-only"
            />
            <span
              aria-hidden
              className={cn(
                "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border",
                value === option.value ? "border-navy-700 bg-navy-700" : "border-ink-400 bg-white",
              )}
            >
              {value === option.value && <span className="size-1.5 rounded-full bg-white" />}
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-medium text-navy-900">{option.label}</span>
              {option.description && (
                <span className="mt-0.5 block text-xs text-ink-500">{option.description}</span>
              )}
            </span>
          </label>
        ))}
      </div>

      {error && (
        <p role="alert" className="mt-2 text-xs font-medium text-danger-500">
          {error}
        </p>
      )}
    </fieldset>
  );
}

/**
 * The honeypot.
 *
 * Hidden from people three ways — off-screen, `tabIndex={-1}` and
 * `aria-hidden` — so no keyboard or screen-reader user can reach it, while a
 * script that fills every input will. `autoComplete="off"` stops a browser
 * helpfully populating it and locking a real visitor out.
 */
export function HoneypotField({ register }: { register: Record<string, unknown> }) {
  return (
    <div aria-hidden className="absolute left-[-9999px] top-0 size-px overflow-hidden">
      <label htmlFor="website">Leave this field empty</label>
      <input id="website" type="text" tabIndex={-1} autoComplete="off" {...register} />
    </div>
  );
}
